import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BatteryMedium,
  Camera,
  Check,
  Copy,
  LayoutDashboard,
  RotateCcw,
  Signal,
  Snowflake,
  Wifi,
} from "lucide-react";
import { CLUB, METHODS, SPECTATOR_BUCKETS, fmt } from "./demoData";
import { BTN_PRIMARY, BTN_SECONDARY, BTN_SMALL, EASE, Kicker, Panel, Segmented } from "./ui";

const METHOD_OPTIONS = METHODS.map((m) => ({ value: m, label: m }));

const PhoneFrame = ({ children }) => (
  <div className="relative w-full max-w-[370px]">
    <div className="absolute inset-0 -z-10 scale-110 rounded-[3rem] bg-gradient-to-br from-brand-orange/25 via-transparent to-brand-blue/25 blur-3xl animate-pulseGlow" />
    <div className="rounded-[2.75rem] border border-white/15 bg-navy-950/80 p-2.5 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)]">
      <div className="overflow-hidden rounded-[2.3rem] border border-white/[0.07] bg-navy-900/90">
        <div className="flex items-center justify-between px-7 pt-3.5 pb-2 text-[11px] font-semibold text-white/60">
          <span className="tabular-nums">16:52</span>
          <span className="h-[22px] w-24 rounded-full bg-black/70" />
          <span className="flex items-center gap-1">
            <Signal className="w-3.5 h-3.5" />
            <Wifi className="w-3.5 h-3.5" />
            <BatteryMedium className="w-4 h-4" />
          </span>
        </div>
        <div className="px-5 pt-3 pb-7 min-h-[600px]">{children}</div>
      </div>
    </div>
  </div>
);

const CheckRow = ({ checked, onToggle, children }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onToggle}
    className="w-full flex items-center gap-3 py-3 border-t border-white/[0.08] text-left text-sm text-white/85"
  >
    <span
      className={`flex items-center justify-center w-5 h-5 shrink-0 rounded-md border transition-colors duration-200 ${
        checked ? "border-transparent bg-origin-border bg-gradient-to-br from-brand-orange to-brand-blue" : "border-white/25"
      }`}
    >
      {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </span>
    {children}
  </button>
);

/** Das Formular für ein Heimspiel. Wird je Spiel neu angelegt (key). */
const EntryForm = ({ game, setup, sponsorName, presence, onDone }) => {
  const started = useRef(Date.now());
  const [bucket, setBucket] = useState(null);
  const [exact, setExact] = useState("");
  const [method, setMethod] = useState("geschätzt");
  const [checks, setChecks] = useState({ durchsage: true, heft: true });
  const [photo, setPhoto] = useState(false);
  const [error, setError] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);

  const tracked = setup.spectators !== "aus";
  const durchsage = setup.blocks.durchsage.on;
  const heft = setup.blocks.heft.on;
  const seconds = () => Math.max(1, Math.round((Date.now() - started.current) / 1000));

  const submit = () => {
    let spectators = null;
    let bucketLabel = null;
    if (tracked) {
      const value = exact.trim();
      if (!bucket && !value) {
        setError("Wähle zuerst eine Zuschauerzahl oder trag die genaue Zahl ein.");
        return;
      }
      if (value && (!/^\d+$/.test(value) || Number(value) > 20000)) {
        setError("Trag eine ganze Zahl zwischen 0 und 20.000 ein.");
        return;
      }
      if (value) {
        spectators = Number(value);
      } else {
        const b = SPECTATOR_BUCKETS.find((x) => x.id === bucket);
        spectators = b.value;
        bucketLabel = b.label;
      }
    }
    onDone({
      n: game.n,
      status: "gespielt",
      spectators,
      bucket: bucketLabel,
      method: tracked ? method : null,
      done: { durchsage: durchsage && checks.durchsage, heft: heft && checks.heft },
      photo,
      seconds: seconds(),
    });
  };

  const cancel = () => onDone({ n: game.n, status: "ausgefallen", seconds: seconds() });

  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-white/45">
        <span>{CLUB.name}</span>
        <span className="rounded-full border border-white/10 px-2 py-0.5">ohne Login</span>
      </div>
      <div className="mt-4 font-display font-bold text-xl text-white">Heimspiel {game.n} · {CLUB.team}</div>
      <div className="mt-0.5 text-xs text-white/45">
        {game.weekday} {game.date} · {game.time} · gegen {game.opponent}
      </div>

      {tracked && (
        <div className="mt-6">
          <div className="text-sm font-semibold text-white">Wie viele Zuschauer ungefähr?</div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {SPECTATOR_BUCKETS.map((b) => {
              const active = bucket === b.id && !exact;
              return (
                <button
                  key={b.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setBucket(b.id);
                    setExact("");
                    setError("");
                  }}
                  className={`rounded-xl border px-1 py-2.5 text-xs font-semibold transition-colors duration-200 ${
                    active
                      ? "border-white/40 bg-white/[0.12] text-white"
                      : "border-white/10 bg-white/[0.02] text-white/65 hover:border-white/25"
                  }`}
                >
                  {b.label}
                </button>
              );
            })}
            <input
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={exact}
              onChange={(e) => {
                setExact(e.target.value);
                setError("");
              }}
              placeholder="genau"
              aria-label="Genaue Zuschauerzahl"
              className="rounded-xl border border-white/10 bg-white/[0.02] px-2 py-2.5 text-center text-xs font-semibold text-white placeholder:text-white/35 outline-none focus:border-brand-blue/60"
            />
          </div>
          <div className="mt-3">
            <span className="block mb-1.5 text-[11px] text-white/40">Grundlage der Zahl</span>
            <Segmented
              size="sm"
              layoutId="demo-entry-method"
              label="Grundlage der Zuschauerzahl"
              options={METHOD_OPTIONS}
              value={method}
              onChange={setMethod}
            />
          </div>
        </div>
      )}

      {(durchsage || heft || presence.length > 0) && (
        <div className="mt-6">
          <div className="text-sm font-semibold text-white mb-1">Heute erbracht</div>
          {durchsage && (
            <CheckRow
              checked={checks.durchsage}
              onToggle={() => setChecks((c) => ({ ...c, durchsage: !c.durchsage }))}
            >
              Durchsage für {sponsorName}
            </CheckRow>
          )}
          {heft && (
            <CheckRow checked={checks.heft} onToggle={() => setChecks((c) => ({ ...c, heft: !c.heft }))}>
              Anzeige im Heft verteilt
            </CheckRow>
          )}
          {presence.length > 0 && (
            <div className="flex items-start gap-3 py-3 border-t border-white/[0.08] text-xs leading-relaxed text-white/45">
              <span className="flex items-center justify-center w-5 h-5 shrink-0 rounded-md border border-white/10 bg-white/[0.05]">
                <Check className="w-3 h-3 text-white/50" strokeWidth={3} />
              </span>
              {presence.join(", ")} laufen automatisch mit
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setPhoto((p) => !p)}
        aria-pressed={photo}
        className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-xs font-semibold transition-colors duration-200 ${
          photo ? "border-white/30 bg-white/[0.08] text-white" : "border-white/10 text-white/60 hover:text-white"
        }`}
      >
        {photo ? <Check className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
        {photo ? "Foto hinzugefügt" : "Foto hinzufügen (optional)"}
      </button>

      <button type="button" onClick={submit} className={`${BTN_PRIMARY} mt-3 w-full`}>
        Fertig
      </button>
      {error && <p className="mt-3 text-xs text-brand-orange">{error}</p>}

      <div className="mt-5 text-center">
        {confirmCancel ? (
          <div className="text-xs text-white/55">
            Als ausgefallen melden?{" "}
            <button type="button" onClick={cancel} className="font-semibold text-white underline underline-offset-4">
              Ja
            </button>{" "}
            ·{" "}
            <button
              type="button"
              onClick={() => setConfirmCancel(false)}
              className="font-semibold text-white/70 underline underline-offset-4"
            >
              Nein
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmCancel(true)}
            className="text-xs text-white/40 underline underline-offset-4 hover:text-white/70"
          >
            Spiel ausgefallen oder verlegt
          </button>
        )}
      </div>
    </div>
  );
};

const Saved = ({ entry, game, hasNext, onNext, onGoto }) => (
  <div className="flex flex-col items-center text-center pt-12">
    <motion.span
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 18 }}
      className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-orange to-brand-blue shadow-[0_0_40px_-6px_rgba(255,107,0,0.7)]"
    >
      <Check className="w-8 h-8 text-white" strokeWidth={3} />
    </motion.span>
    <div className="mt-6 font-display font-bold text-2xl text-white">Gespeichert</div>
    <p className="mt-2 text-sm leading-relaxed text-white/55">
      {entry.status === "ausgefallen"
        ? `Heimspiel ${entry.n} gegen ${game.opponent} ist als ausgefallen gemeldet.`
        : `Heimspiel ${entry.n} gegen ${game.opponent}${
            entry.spectators != null ? `, ${fmt(entry.spectators)} Zuschauer` : ""
          }.`}
    </p>
    <div className="mt-7 w-full rounded-2xl border border-white/10 px-5 py-5">
      <div className="font-display font-black text-5xl gradient-text tabular-nums">{entry.seconds} s</div>
      <div className="mt-1 text-xs text-white/45">hat der Eintrag gedauert</div>
    </div>
    <button type="button" onClick={() => onGoto("dashboard")} className={`${BTN_PRIMARY} mt-8 w-full`}>
      <LayoutDashboard className="w-4 h-4" />
      Im Dashboard ansehen
    </button>
    {hasNext && (
      <button type="button" onClick={onNext} className={`${BTN_SECONDARY} mt-3 w-full`}>
        Nächstes Spiel eintragen
        <ArrowRight className="w-4 h-4" />
      </button>
    )}
  </div>
);

const WinterBreak = ({ onGoto, onReset }) => (
  <div className="flex flex-col items-center text-center pt-14">
    <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/15 bg-white/[0.05]">
      <Snowflake className="w-7 h-7 text-[#6FB1FF]" />
    </span>
    <div className="mt-6 font-display font-bold text-2xl text-white">Winterpause</div>
    <p className="mt-2 text-sm leading-relaxed text-white/55">
      Alle Heimspiele bis zur Winterpause sind eingetragen. Weiter geht es am 07.03.
    </p>
    <button type="button" onClick={() => onGoto("dashboard")} className={`${BTN_PRIMARY} mt-8 w-full`}>
      <LayoutDashboard className="w-4 h-4" />
      Im Dashboard ansehen
    </button>
    <button type="button" onClick={onReset} className={`${BTN_SECONDARY} mt-3 w-full`}>
      <RotateCcw className="w-4 h-4" />
      Einträge zurücksetzen
    </button>
  </div>
);

const NothingToEnter = ({ onGoto }) => (
  <div className="flex flex-col items-center text-center pt-14">
    <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/15 bg-white/[0.05]">
      <Check className="w-7 h-7 text-white/70" />
    </span>
    <div className="mt-6 font-display font-bold text-2xl text-white">Nichts einzutragen</div>
    <p className="mt-2 text-sm leading-relaxed text-white/55">
      In diesem Setup gibt es am Spieltag nichts zu erfassen. Präsenz und Online-Beiträge laufen automatisch mit.
    </p>
    <button type="button" onClick={() => onGoto("setup")} className={`${BTN_SECONDARY} mt-8 w-full`}>
      Setup anpassen
    </button>
  </div>
);

const HELPERS = [
  { id: "kasse", label: "Kasse Hauptplatz", scope: "alle Heimspiele der 1. Herren" },
  { id: "platzwart", label: "Platzwart", scope: "alle Heimspiele, Fotos der Bande" },
  { id: "jugend", label: "Jugendleitung", scope: "Spiele der Jugendmannschaften", youth: true },
];

export const DemoEntry = ({ data, onSubmit, onGoto, onResetEntries }) => {
  const [saved, setSaved] = useState(null);
  const [copied, setCopied] = useState(null);
  const { setup, sponsor, nextGame, tasks } = data;
  const b = setup.blocks;

  const presence = [
    b.bande.on && "Bande",
    b.banner.on && "Banner",
    b.trikot.on && "Trikot",
  ].filter(Boolean);
  const nothingToEnter = setup.spectators === "aus" && !b.durchsage.on && !b.heft.on;
  const savedGame = saved ? data.games.find((g) => g.n === saved.n) : null;
  const helpers = HELPERS.filter((h) => !h.youth || setup.spectators === "alle");

  const handleDone = (entry) => {
    onSubmit(entry);
    setSaved(entry);
  };

  let screen;
  if (saved && savedGame) {
    screen = (
      <Saved entry={saved} game={savedGame} hasNext={Boolean(nextGame)} onNext={() => setSaved(null)} onGoto={onGoto} />
    );
  } else if (nothingToEnter) {
    screen = <NothingToEnter onGoto={onGoto} />;
  } else if (!nextGame) {
    screen = (
      <WinterBreak
        onGoto={onGoto}
        onReset={() => {
          onResetEntries();
          setSaved(null);
        }}
      />
    );
  } else {
    screen = (
      <EntryForm
        key={nextGame.n}
        game={nextGame}
        setup={setup}
        sponsorName={sponsor.name}
        presence={presence}
        onDone={handleDone}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-sticky space-y-6">
          <div>
            <Kicker>Sicht Verein</Kicker>
            <h3 className="mt-3 font-display font-bold text-3xl md:text-4xl leading-tight text-white">
              Eintragen in unter <span className="gradient-text whitespace-nowrap">30 Sekunden</span>.
            </h3>
            <p className="mt-4 text-base leading-relaxed text-white/55">
              Nach jedem Heimspiel kommt ein Link aufs Handy, ohne Anmeldung. Wer gerade da ist, trägt ein: Kasse,
              Platzwart oder Trainer. Probiert es aus, der Eintrag landet sofort im Dashboard.
            </p>
          </div>

          <Panel className="p-6">
            <Kicker>Offene Aufgaben</Kicker>
            <ul className="mt-3 divide-y divide-white/[0.07]">
              {tasks.map((t) => {
                const Icon = t.icon;
                return (
                  <li key={t.id} className="py-3 flex items-start gap-3">
                    <span className="mt-0.5 flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-white/[0.05] border border-white/10">
                      <Icon className="w-3.5 h-3.5 text-white/65" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-white">{t.title}</span>
                      <span className="block text-xs text-white/45">{t.detail}</span>
                    </span>
                    {t.action && (
                      <span className="mt-1 text-[10px] uppercase tracking-wider font-bold text-brand-orange">jetzt</span>
                    )}
                  </li>
                );
              })}
              {tasks.length === 0 && <li className="py-3 text-sm text-white/45">Alles erledigt.</li>}
            </ul>
          </Panel>

          <Panel className="p-6">
            <Kicker>Helfer-Links</Kicker>
            <p className="mt-2 text-xs leading-relaxed text-white/45">
              Jeder Link gilt nur für diesen Verein und lässt sich jederzeit widerrufen. Am Eintrag steht, über welchen
              Link er kam.
            </p>
            <ul className="mt-4 space-y-2.5">
              {helpers.map((h) => (
                <li
                  key={h.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-white/10 px-4 py-2.5"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-white">{h.label}</span>
                    <span className="block text-[11px] text-white/40">{h.scope}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setCopied(h.id)}
                    className={`${BTN_SMALL} shrink-0 !px-3 !py-1.5`}
                  >
                    {copied === h.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === h.id ? "Geteilt" : "Teilen"}
                  </button>
                </li>
              ))}
            </ul>
            {copied && (
              <p className="mt-3 text-xs text-white/40">In der Demo wird kein echter Link erzeugt.</p>
            )}
          </Panel>
        </div>
      </div>

      <div className="lg:col-span-7 flex justify-center lg:pt-4">
        <PhoneFrame>
          <motion.div
            key={saved ? `saved-${saved.n}` : nextGame ? `form-${nextGame.n}` : "end"}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            {screen}
          </motion.div>
        </PhoneFrame>
      </div>
    </div>
  );
};
